import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import template from './calculations.hbs'
import rowTemplate from './calculations-row.hbs'
import formatValue from 'scoreboard/components/format-value'
import conductor from 'scoreboard/components/conductor'
import config from 'scoreboard/components/config'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'
import i18n from 'scoreboard/components/i18n'
import constants from 'scoreboard/components/constants'

var DataUsedInCalculations = Backbone.View.extend({

	/**
	 * The Data Used in Calculations panel on the Scorecards Overview
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {ScorecardNode} options.model
	 */
	initialize: function(options = {}) {
		this.options = options
		this.snById = _.keyBy(this.model.get('dataUsedInCalculations'), 'id')
	},

	className: 'panel panel-with-table',

	events: {
		'click tr[data-id]': 'clickRow'
	},

	render: function() {
		if (this._rendered) {
			return this.rerender()
		}

		this.$el.empty().append(template())

		var scorecardNodes = _.clone(this.model.get('dataUsedInCalculations'))
		var html = ''
		_.each(scorecardNodes, function(sn) {
			html += this.rowHtml(sn)
		}.bind(this))
		this.$('table tbody').append(html)

		if (!config.scoresVisible) {
			this.$('.score').hide()
		}

		var descendants = _.size(scorecardNodes) > 0
		this.$('.table').toggleClass('is-not-displayed', !descendants)
		this.$('.min-height-vertical-center').toggleClass('is-not-displayed', descendants)

		this.setupWeightingTooltips()

		this._rendered = true
		return this
	},

	rowHtml: function(sn) {
		let context = _.defaults({
			color: sn.color || 'gray',
			percentWeight: this._formatWeight(sn),
			score: this._formatScore(sn),
			scoresVisible: config.scoresVisible
		}, sn)
		return rowTemplate(context)
	},

	_formatScore: function(sn) {
		return formatValue({
			value: sn.score,
			precision: this.model.get('precision'),
			dataType: 'score'
		})
	},

	_formatWeight: function(sn) {
		let percentWeighting = sn.percentWeighting
		// missing percent weightings are actually 0, as long as the status is
		// weighted. we're looking at the parent's status which is OK because
		// the status for the entire scorecard is recalculated at once
		if (_.isNil(percentWeighting)) {
			percentWeighting = 0
		}
		return formatValue({
			value: percentWeighting,
			dataType: 'percent',
			precision: this.model.get('precision')
		})
	},

	// rather than rebuild the entire table, change just the minimum required.
	// this is needed for CSS transitions to work
	rerender: function() {
		this.snById = _.keyBy(this.model.get('dataUsedInCalculations'), 'id')
		// update existing rows, and remove any that are no longer relevant
		_.each(this.$('tr[data-id]'), function(row) {
			row = $(row)
			var id = row.data('id')
			var sn = this.snById[id]
			if (sn) {
				var color = sn.color || 'gray'
				row.find('.label')
					.attr('class', 'label ' + color)
				row.find('.score')
					.text(this._formatScore(sn))
					.attr('class', 'score ' + color)
				row.find('.weight span').text(this._formatWeight(sn))
			} else {
				row.remove()
			}
		}.bind(this))

		// add any missing rows
		_.each(this.model.get('dataUsedInCalculations'), function(sn, i) {
			// do fresh dom query in case we added some rows
			var row = this.$('tr[data-id]').eq(i)
			var id = row.data('id')
			if (id == sn.id) {
				row.find('td:first-child').attr('class', 'label ' + (this.snById[id].color || 'gray'))
			} else {
				// wrong ID means we need to add a row
				if (_.isEmpty(row)) {
					this.$('tbody').append(this.rowHtml(sn))
				} else {
					row.before(this.rowHtml(sn))
				}
			}
		}.bind(this))

		this.setupWeightingTooltips()

		return this
	},

	clickRow: function(e) {
		var id = $(e.currentTarget).data('id')
		conductor.overview('scorecards', id)
	},

	/**
	 * Sets up tooltips that explain why some metrics have 0% weighting
	 * (#24555). The weighting can be 0% in a few circumstances:
	 * 1) The scorecard item's weight was manually set to 0
	 * 2) The scorecard item is unscored
	 * 3) The score was gray and we are ignoring gray scores, therefore the
	 * scorecard item didn't factor into the current period's calculations
	 */
	setupWeightingTooltips: function() {
		if (!config.ignoreGrayScoresInRollups) return

		let elements = this.$('td.weight') // use td to exclude the header
		elements.find('span').removeClass('text-link-emphasized')
		elements = _.filter(elements, el => {
			let id = this.$(el).closest('[data-id]').data('id')
			let sn = this.snById[id]
			let scoringType = config.scoringType(sn.scoringTypeId)
			// #50274 do not show tooltip for unscored OR unscored text scoring types
			let isUnscored =
				_.get(scoringType, 'standardScoringType') == constants.UNSCORED ||
				_.get(scoringType, 'kpiValueType') == constants.UNSCORED_TEXT
			return _.isNil(sn.score) &&
				_.isNil(sn.percentWeighting) &&
				!isUnscored &&
				this.model.get('status') === 'weighted'
		})
		this.$(elements).find('span').addClass('text-link-emphasized')

		_.invokeMap(this.weightTooltips, 'remove')
		this.weightTooltips = []
		_.each(elements, el => {
			this.weightTooltips.push(new TooltipAndDetails({
				el: this.el,
				selector: el,
				tooltipOptions: {
					extraClass: 'details-only'
				},
				tooltipContent: () => {
					let zeroPercent = formatValue({
						value: 0,
						dataType: 'percent'
					})
					return i18n.get('scorecards.node.weight.zeroBecauseIgnored',
						zeroPercent)
				}
			}))
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap(this.weightTooltips, 'remove')
	}

})

export default DataUsedInCalculations

DataUsedInCalculations.__filename__ = 'scorecards/overview/calculations.js'
